import * as React from "react";
import { ExternalLink } from "lucide-react";

export const VisitSourceButton: React.FC<{ href: string }> = ({ href }) => {
  const safeHref = getSafeCitationHref(href);
  if (!safeHref) return null;
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const popup = window.open(safeHref, "_blank", buildVisitSourceWindowFeatures());
    if (!popup) return;

    event.preventDefault();
    try {
      popup.opener = null;
      popup.focus();
    } catch {
      // Non-critical: some hosts restrict cross-window access immediately.
    }
  };

  return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="inline-flex items-center gap-1.5 self-start rounded-lg bg-primary px-2.5 py-1.5 text-[12px] font-semibold text-primary-foreground shadow-sm shadow-black/10 ring-1 ring-primary/10 transition hover:opacity-90"
    >
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      Visit source
    </a>
  );
};

export function getSafeCitationHref(rawHref: string | null | undefined): string {
  const href = String(rawHref ?? "").trim();
  if (!href) return "";
  if (/^https?:\/\//i.test(href)) return href;
  if (/^\/api\/(?:gcpfile|web-pdf)\//i.test(href)) return href;
  return "";
}

function buildVisitSourceWindowFeatures(): string {
  const screen = window.screen as Screen & Partial<{ availLeft: number; availTop: number }>;
  const availableLeft = screen.availLeft ?? 0;
  const availableTop = screen.availTop ?? 0;
  const availableScreenWidth = screen.availWidth || window.innerWidth || 1280;
  const availableScreenHeight = screen.availHeight || window.innerHeight || 900;
  const margin = 64;
  const width = Math.round(clampNumber(1220, 980, Math.max(980, availableScreenWidth - margin)));
  const height = Math.round(clampNumber(860, 720, Math.max(720, availableScreenHeight - margin)));
  const left = Math.max(0, Math.round(availableLeft + (availableScreenWidth - width) / 2));
  const top = Math.max(0, Math.round(availableTop + (availableScreenHeight - height) / 2));

  return [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    `screenX=${left}`,
    `screenY=${top}`,
    "menubar=yes",
    "toolbar=yes",
    "location=yes",
    "status=yes",
    "resizable=yes",
    "scrollbars=yes",
  ].join(",");
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
